import { MultiPlaceRetrospectiveData, RetrospectiveData } from "./types";
import { useGetRetrospective } from "./use-get-retrospective";
import RootStore from "#stores/index";
import * as React from "react";
import { useObserver } from "mobx-react-lite";
import {
	transformApiResponseToMultiPlaceData,
	transformApiResponseToRetrospectiveData,
} from "./transformation";

interface UseRetrospectiveDataParams {
	placeId?: string;
	year: number;
	enabled?: boolean;
}

export interface UseRetrospectiveDataResult {
	data?: RetrospectiveData;
	multiPlaceData?: MultiPlaceRetrospectiveData;
	isLoading: boolean;
	error: Error | null;
	hasNoData: boolean;
	refetch: () => void;
}

export function useRetrospectiveData({
	placeId,
	year,
	enabled = true,
}: UseRetrospectiveDataParams): UseRetrospectiveDataResult {
	const { placeStore } = React.useContext(RootStore);

	const placesState = useObserver(() => {
		const promiseState = placeStore.fetchPlacesPromise?.state;
		const isFetching = promiseState === "pending";
		const isFetched =
			promiseState === "fulfilled" ||
			(!placeStore.fetchPlacesPromise && placeStore.places.length > 0);

		return {
			places: placeStore.places,
			placesLength: placeStore.places.length,
			isLoading: isFetching,
			isFetched,
		};
	});

	React.useEffect(() => {
		if (!placeId && placesState.placesLength === 0 && !placesState.isLoading) {
			placeStore.fetchPlaces();
		}
	}, [placeId, placesState.placesLength, placesState.isLoading, placeStore]);

	const userPlaceIds = useObserver(() => {
		if (placeId) {
			return [placeId];
		}
		return placeStore.places.map(place => place.id);
	});

	const shouldEnableQuery =
		enabled &&
		userPlaceIds.length > 0 &&
		(placeId !== undefined || placesState.isFetched);

	const { data: apiData, isLoading, error, refetch } = useGetRetrospective(
		{
			year,
			placeIds: userPlaceIds,
		},
		{
			enabled: shouldEnableQuery,
		},
	);

	const processedData = React.useMemo(() => {
		if (!apiData) return { data: undefined, multiPlaceData: undefined };

		const bigNumbersArray = apiData.bigNumbers || [];

		if (bigNumbersArray.length > 1) {
			const multiPlaceData = transformApiResponseToMultiPlaceData(apiData);
			return {
				data: multiPlaceData.aggregated,
				multiPlaceData,
			};
		}

		return {
			data: transformApiResponseToRetrospectiveData(apiData),
			multiPlaceData: undefined,
		};
	}, [apiData]);

	const hasNoData = React.useMemo(() => {
		if (!apiData) return true;
		const bigNumbersArray = apiData.bigNumbers || [];
		return bigNumbersArray.length === 0;
	}, [apiData]);

	return {
		...processedData,
		isLoading,
		error: error as Error | null,
		hasNoData,
		refetch,
	};
}
